"use client";

import { ArrowRight, UserPlus } from "lucide-react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { type ReactNode, useEffect, useRef } from "react";
import Avatar from "sigma-avatars";
import { toast } from "sonner";
import OptimizedImage from "@/components/optimized-image";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@/components/ui/empty";
import { Spinner } from "@/components/ui/spinner";
import { useIdentity } from "@/contexts/identity-context";
import { useBapProfiles } from "@/hooks/use-bap-profile";
import { useSession } from "@/hooks/use-session";
import { authClient } from "@/lib/auth-client";
import { registerBackupIdentitiesForCurrentSession } from "@/lib/auth-flows";
import { getDisplayName, getMergedProfile } from "@/lib/bap/display";
import { DISPLAY_IMAGE, getDisplayImageUrl } from "@/lib/image-utils";
import { getOAuthCallbackErrorMessage } from "@/lib/oauth-account-linking-errors";
import { clearSensitiveSessionStorage } from "@/lib/storage/sensitive-storage";
import { STORAGE_KEYS } from "@/lib/storage-keys";
import { fetchUserStatus } from "@/lib/user-status";

/** Error card with optional debug info */
function ErrorCard({
	error,
	errorDescription,
	provider,
}: {
	error: string;
	errorDescription: string | null;
	provider: string | null;
}) {
	const router = useRouter();

	return (
		<Card className="mx-auto max-w-md border-destructive bg-destructive/10">
			<CardHeader>
				<CardTitle className="text-destructive">Authentication Error</CardTitle>
				<CardDescription>
					{getOAuthCallbackErrorMessage({
						error,
						errorDescription,
						provider,
					}) ?? "An error occurred during authentication"}
				</CardDescription>
			</CardHeader>
			<CardContent>
				<Button onClick={() => router.push("/")} size="sm" variant="outline">
					Try Again
				</Button>
			</CardContent>
		</Card>
	);
}

/** Check if wallet needs to be unlocked and redirect if so */
function useWalletLockRedirect(error: string | null) {
	const router = useRouter();

	useEffect(() => {
		if (error) {
			return;
		}

		const hasEncryptedBackup = !!localStorage.getItem(
			STORAGE_KEYS.ENCRYPTED_BACKUP
		);
		const hasDecryptedBackup = !!sessionStorage.getItem(
			STORAGE_KEYS.DECRYPTED_BACKUP
		);

		if (hasEncryptedBackup && !hasDecryptedBackup) {
			router.push("/login?return_to=/");
		}
	}, [error, router]);
}

/** Redirect to onboarding if the user hasn't completed it */
function useOnboardingGuard(
	sessionLoading: boolean,
	isAuthenticated: boolean,
	error: string | null
) {
	const router = useRouter();

	useEffect(() => {
		if (sessionLoading || !isAuthenticated || error) {
			return;
		}

		fetchUserStatus()
			.then((status) => {
				if (status && status.onboarding_completed === false) {
					router.push("/onboarding");
				}
			})
			.catch(() => {
				// Non-critical — other guards will handle auth issues
			});
	}, [sessionLoading, isAuthenticated, error, router]);
}

/** Recover orphaned sessions when possible; sign out only without local material */
function useOrphanedSessionRecovery(
	sessionLoading: boolean,
	isAuthenticated: boolean,
	identitiesCount: number
) {
	const router = useRouter();
	const repairAttemptedRef = useRef(false);

	useEffect(() => {
		if (sessionLoading || !isAuthenticated || identitiesCount > 0) {
			return;
		}

		const decryptedBackupStr = sessionStorage.getItem(
			STORAGE_KEYS.DECRYPTED_BACKUP
		);
		if (decryptedBackupStr) {
			if (repairAttemptedRef.current) {
				return;
			}
			repairAttemptedRef.current = true;

			try {
				const backup = JSON.parse(decryptedBackupStr);
				registerBackupIdentitiesForCurrentSession(backup)
					.then(() => {
						window.dispatchEvent(new Event("bap-backup-updated"));
						router.refresh();
					})
					.catch(() => {
						console.error("Orphaned session identity repair failed");
						toast.error(
							"Could not restore your identity mapping. Please retry sign-in."
						);
					});
			} catch {
				console.error("Failed to parse decrypted backup for repair");
				toast.error(
					"Could not restore your identity mapping. Please retry sign-in."
				);
			}
			return;
		}

		const hasEncryptedBackup = !!localStorage.getItem(
			STORAGE_KEYS.ENCRYPTED_BACKUP
		);

		if (!hasEncryptedBackup) {
			console.log(
				"No local backup found but user is authenticated - signing out for fresh start"
			);
			clearSensitiveSessionStorage();
			authClient.signOut().then(() => router.push("/login"));
		}
	}, [sessionLoading, isAuthenticated, identitiesCount, router]);
}

interface HomeClientProps {
	/** Server-side session verdict, used to pick what to show before the client session resolves */
	initialAuthenticated: boolean;
	/** Static landing content rendered by the server page; shown to signed-out visitors */
	landing: ReactNode;
}

/**
 * Session-dependent portion of the homepage. Extracted from app/page.tsx so
 * the page itself can be a server component with crawlable static HTML;
 * this island handles everything that needs searchParams, session state,
 * and local storage.
 */
export function HomeClient({ initialAuthenticated, landing }: HomeClientProps) {
	const router = useRouter();
	const searchParams = useSearchParams();
	const { user: sessionUser, isLoading: sessionLoading } = useSession();

	// Read error params from URL (Better Auth redirects errors here in production)
	const error = searchParams.get("error");
	const errorDescription = searchParams.get("error_description");
	const provider = searchParams.get("provider");

	// Setup readiness is resolved server-side in app/page.tsx, so the only
	// thing left to wait on here is the session.
	const isAuthenticated = !!sessionUser;
	const loading = sessionLoading;

	// Use identity context for consistent data source
	const { identities } = useIdentity();
	const profileQueries = useBapProfiles(identities.map((id) => id.bapId));

	// Redirect if wallet is locked
	useWalletLockRedirect(error);

	// Redirect to onboarding if not completed
	useOnboardingGuard(sessionLoading, isAuthenticated, error);

	// Detect new device and sign out if needed
	useOrphanedSessionRecovery(
		sessionLoading,
		isAuthenticated,
		identities.length
	);

	// Before the client session resolves, trust the server's verdict so
	// signed-out visitors get the landing page immediately (no spinner) and
	// signed-in users never see a marketing flash.
	const showLanding = loading ? !initialAuthenticated : !isAuthenticated;
	const showAccounts = !loading && isAuthenticated;
	const showSpinner = loading && initialAuthenticated;

	return (
		<div className="space-y-10">
			{error && (
				<div className="pt-10">
					<ErrorCard
						error={error}
						errorDescription={errorDescription}
						provider={provider}
					/>
				</div>
			)}

			{showSpinner && (
				<div className="flex justify-center py-24">
					<Spinner className="h-6 w-6" />
				</div>
			)}

			{showAccounts && (
				<section className="mx-auto max-w-2xl pt-12 md:pt-20">
					<div className="mb-6">
						<p className="font-mono text-muted-foreground text-xs uppercase tracking-[0.2em]">
							Signed in
						</p>
						<h1 className="mt-2 font-semibold text-2xl tracking-tight">
							Your identities
						</h1>
						<p className="mt-1 text-muted-foreground text-sm">
							Choose an identity to manage its profile, keys, and connected
							apps.
						</p>
					</div>

					{identities.length > 0 && (
						<ul className="divide-y border">
							{identities.map((identity, index) => {
								const fetchedProfile = profileQueries[index]?.data ?? null;
								const profile = getMergedProfile(identity, fetchedProfile);
								const displayName = getDisplayName(profile, identity);
								const imageUrl = profile?.identity?.image
									? getDisplayImageUrl(
											profile.identity.image,
											null,
											DISPLAY_IMAGE.avatarHero
										)
									: null;

								return (
									<li key={identity.bapId}>
										<Link
											className="group flex items-center gap-4 p-4 transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none"
											href={`/account/${encodeURIComponent(identity.bapId)}`}
										>
											{imageUrl ? (
												<OptimizedImage
													alt=""
													className="h-12 w-12 shrink-0 rounded-full object-cover ring-1 ring-border"
													height={48}
													quality={90}
													src={imageUrl}
													width={48}
												/>
											) : (
												<div className="h-12 w-12 shrink-0 overflow-hidden rounded-full ring-1 ring-border">
													<Avatar
														colors={[
															"var(--chart-1)",
															"var(--chart-2)",
															"var(--chart-3)",
															"var(--chart-4)",
															"var(--chart-5)",
														]}
														name={identity.bapId}
														size={48}
														variant="pixel"
													/>
												</div>
											)}
											<span className="flex min-w-0 flex-1 flex-col gap-0.5">
												<span className="truncate font-medium">
													{displayName}
												</span>
												<span className="truncate font-mono text-muted-foreground text-xs">
													{identity.bapId}
												</span>
											</span>
											<ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
										</Link>
									</li>
								);
							})}
						</ul>
					)}

					{identities.length === 0 && (
						<Card>
							<CardContent className="py-8">
								<Empty>
									<EmptyHeader>
										<EmptyMedia variant="icon">
											<UserPlus className="h-10 w-10" />
										</EmptyMedia>
										<EmptyTitle>No identities yet</EmptyTitle>
										<EmptyDescription>
											Create a Bitcoin identity to use with OAuth applications.
										</EmptyDescription>
									</EmptyHeader>
									<EmptyContent>
										<Button onClick={() => router.push("/signup")}>
											Create an identity
										</Button>
									</EmptyContent>
								</Empty>
							</CardContent>
						</Card>
					)}
				</section>
			)}

			{showLanding && landing}
		</div>
	);
}
